import { ignoreBestEffortFailure } from "@/lib/ignored-failure";
import { useToast } from "@/hooks/use-toast";
import { useDropCacheOnIdentityChange } from "@/hooks/use-drop-cache-on-identity-change";
import { InsertUser, User as SelectUser } from "@shared/platform/schema";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";
import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { signOut, supabase } from "../../services/supabase";
import { apiRequest, getQueryFn, queryClient, setCurrentToken } from "../lib/queryClient";
import { userDisplayName } from "../lib/utils";
import { API_PATHS } from "@shared/platform/app-metadata";

import { TOAST_TITLES, USE_AUTH_COPY } from "@/lib/ui-labels";
type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, InsertUser>;
  supabaseUser: SupabaseUser | null;
  accessToken: string | null;
};

type LoginData = Pick<InsertUser, "email" | "password">;

interface LoginResponse {
  success: boolean;
  user?: SelectUser;
  session?: Session;
  message?: string;
}

const SUPABASE_STORAGE_KEY_PREFIX = 'sb-';

export function wipeStoredSupabaseSession(): void {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(SUPABASE_STORAGE_KEY_PREFIX)) localStorage.removeItem(key);
  });
  sessionStorage.removeItem('chem_access_token');
}

export const AuthContext = createContext<AuthContextType | null>(null);
export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isInitialAuthCheckLoading, setIsInitialAuthCheckLoading] = useState(true);

  useEffect(() => {
    console.log("[AuthProvider] Setting up onAuthStateChange listener.");
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: string, session: Session | null) => {
        console.log(`[AuthProvider] onAuthStateChange event: ${event}`);
        const currentToken = session?.access_token || null;
        setAccessToken(currentToken);
        setCurrentToken(currentToken);
        
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
          setSupabaseUser(session?.user || null);
          if (currentToken) {
             console.log("[AuthProvider] Auth state change indicates signed in, invalidating /api/user.");
             queryClient.invalidateQueries({ queryKey: [API_PATHS.user, currentToken] });
          } else {
             queryClient.setQueryData([API_PATHS.user, null], null);
          }
        } else if (event === 'SIGNED_OUT') {
          setSupabaseUser(null);
          queryClient.setQueryData([API_PATHS.user, null], null); 
        }
        if (event === 'INITIAL_SESSION') {
             console.log("[AuthProvider] Initial session processed.");
             setIsInitialAuthCheckLoading(false);
        }
      }
    );

    return () => {
      console.log("[AuthProvider] Unsubscribing from onAuthStateChange.");
      subscription.unsubscribe();
    };
  }, []);

  const {
    data: user,
    error,
    isLoading: isUserQueryLoading,
  } = useQuery<SelectUser | null, Error>({
    queryKey: [API_PATHS.user, accessToken],
    queryFn: getQueryFn({ on401: "returnNull" }), 
    enabled: !isInitialAuthCheckLoading, 
    staleTime: 1000 * 60 * 5,
  });
  useDropCacheOnIdentityChange(user);

  const isLoading = isInitialAuthCheckLoading || (accessToken != null && isUserQueryLoading);

  const applyLoginSession = async (loginResult: LoginResponse) => {
    const token = loginResult.session?.access_token || null;
    setAccessToken(token);
    setCurrentToken(token);

    if (token && loginResult.user) {
      queryClient.setQueryData([API_PATHS.user, token], loginResult.user);
    }

    if (loginResult.session) {
      await supabase.auth.setSession(loginResult.session);
    }
  };

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      console.log("[Auth] Login attempt with email:", credentials.email);
      const res = await apiRequest("POST", API_PATHS.login, credentials);
      const loginResult = (await res.json()) as LoginResponse;
      console.log("Backend /api/login successful:", loginResult);
      
      if (loginResult.success) {
        await applyLoginSession(loginResult);
      }

      if (!loginResult.user) {
        throw new Error(loginResult.message || USE_AUTH_COPY.loginResponseDidNotIncludeA);
      }

      return loginResult.user;
    },
    onSuccess: (loggedInUser: SelectUser, credentials: LoginData) => {
      console.log("Login mutation successful, setting user data:", loggedInUser);
      toast({
        title: USE_AUTH_COPY.loginSuccessful,
        description: `Hello, ${credentials.email}`,
      });
      navigate("/");
    },
    onError: (error: Error) => {
      console.error("Login mutation error:", error);
      const displayMessage = error instanceof Error ? error.message : USE_AUTH_COPY.anUnknownErrorOccurred;
      toast({
        title: TOAST_TITLES.loginFailed,
        description: displayMessage,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (userData: InsertUser) => {
      const res = await apiRequest("POST", API_PATHS.register, userData);
      return await res.json();
    },
    onSuccess: (user: SelectUser) => {
      queryClient.setQueryData([API_PATHS.user], user);
      toast({
        title: USE_AUTH_COPY.registrationSuccessful,
        description: `Welcome, ${userDisplayName(user)}!`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: USE_AUTH_COPY.registrationFailed,
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      console.log("Logging out...");
      try {
        await apiRequest("POST", API_PATHS.logout);
        console.log("Successfully logged out from API");
      } catch (err) {
        console.error("Error during server logout:", err);
      }
      
      wipeStoredSupabaseSession();

      try {
        await signOut();
      } catch {
        ignoreBestEffortFailure();
      }

      window.location.replace('/auth');
      
      return;
    },
    onSuccess: () => {
      queryClient.setQueryData([API_PATHS.user], null);
      toast({
        title: USE_AUTH_COPY.loggedOutSuccessfully,
      });
    },
    onError: (error: Error) => {
      console.error("Logout error:", error);
      toast({
        title: USE_AUTH_COPY.loggingOut,
        description: USE_AUTH_COPY.youWillBeRedirectedToThe,
      });
      
      queryClient.invalidateQueries({ queryKey: [API_PATHS.user] });
      navigate('/auth');
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        supabaseUser,
        accessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
